/**
 * Timing-only scoring. Framework-free by design: no React, no DOM, no Bun
 * APIs. It takes two arrays of numbers and a config, and returns a result.
 * That constraint is what makes it unit-testable against synthetic onsets and
 * portable to a native client later.
 *
 * The central idea, restated because it drives every decision below: we score
 * the RESIDUAL from the player's own best-fit line, not the raw deviation from
 * the recording. A player who is uniformly 8% fast held the clock perfectly —
 * they just held a slightly different clock. Raw-deviation scoring fails
 * everyone within three bars, because unreferenced human tempo error is large
 * and accumulates linearly.
 */

import type { ScoringConfig, Tier } from "./config.ts";
import { DEFAULT_CONFIG } from "./config.ts";

export interface MatchPair {
  expectedIndex: number;
  detectedIndex: number;
  expected: number;
  detected: number;
  /** detected - (a * expected + b). The thing we actually score. */
  residual: number;
  /** detected - expected. Shown to the player as "raw", never scored. */
  rawDeviation: number;
  points: number;
  streakAtThisPoint: number;
  multiplierApplied: number;
}

export interface ScoreResult {
  /** 0..100 before the tier multiplier. */
  baseScore: number;
  /** What the player sees. baseScore * tier, clamped to 0..100 * tier. */
  score: number;
  tier: Tier;
  tierMultiplier: number;

  /** Tempo scale. 1.08 means the player sang 8% fast. */
  tempoScale: number;
  /** Constant offset in seconds. Negative means they came in early. */
  offset: number;
  /** True when the fit was degenerate and we fell back to a = 1. */
  tempoFitFellBack: boolean;

  pairs: MatchPair[];
  missedExpected: number[];
  extraDetected: number[];

  /** Fraction of expected onsets that found a partner. */
  coverage: number;
  /** RMS of residuals over matched pairs, seconds. The headline "tightness". */
  residualRms: number;
  /** Median absolute residual — less bullied by one bad onset than RMS. */
  residualMedianAbs: number;
  /** Longest run of consecutive onsets under the streak threshold. */
  bestStreak: number;
  /** Max streak multiplier reached. */
  peakMultiplier: number;

  /** Set when coverage was too low to score honestly. score is forced to 0. */
  rejected: null | { reason: "coverage" | "no-onsets"; detail: string };

  /** Human-readable secondary stats for the results screen. */
  summary: {
    rushedPct: number;
    earlyMs: number;
    hitCount: number;
    missCount: number;
    extraCount: number;
  };
}

/* ---------------------------------------------------------------- matching */

interface MatchOutput {
  pairs: Array<{ e: number; d: number; ei: number; di: number }>;
  missed: number[];
  extra: number[];
}

/**
 * Monotonic alignment by dynamic programming. Order-preserving: you cannot
 * match your third word to the recording's first. Costs are in seconds, so
 * missCost/extraCost read directly as "how many seconds of timing error is
 * skipping a word worth".
 *
 * A greedy nearest-match would be simpler and is what the spec allows, but it
 * makes a systematic error we care about: once a rushing player's onsets slip
 * past a word boundary, greedy pairs each detection with the wrong expected
 * onset and never recovers, which reads as "drifting" for a player who was
 * merely fast. DP gets this right for ~30 lines of extra code.
 */
function matchMonotonic(
  expected: number[],
  detected: number[],
  window: number,
  cfg: ScoringConfig,
  /** Transform applied to expected before comparison (the fitted line). */
  warp: (e: number) => number,
): MatchOutput {
  const n = expected.length;
  const m = detected.length;
  const MISS = cfg.match.missCost;
  const EXTRA = cfg.match.extraCost;

  // dp[i][j] = best cost aligning first i expected with first j detected.
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(Infinity),
  );
  // 0 = match, 1 = miss (consume expected), 2 = extra (consume detected)
  const bt: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(-1),
  );

  dp[0]![0] = 0;
  for (let i = 1; i <= n; i++) {
    dp[i]![0] = dp[i - 1]![0]! + MISS;
    bt[i]![0] = 1;
  }
  for (let j = 1; j <= m; j++) {
    dp[0]![j] = dp[0]![j - 1]! + EXTRA;
    bt[0]![j] = 2;
  }

  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      let best = dp[i - 1]![j]! + MISS;
      let op = 1;

      const viaExtra = dp[i]![j - 1]! + EXTRA;
      if (viaExtra < best) {
        best = viaExtra;
        op = 2;
      }

      const dev = Math.abs(detected[j - 1]! - warp(expected[i - 1]!));
      if (dev <= window) {
        const viaMatch = dp[i - 1]![j - 1]! + dev;
        if (viaMatch < best) {
          best = viaMatch;
          op = 0;
        }
      }

      dp[i]![j] = best;
      bt[i]![j] = op;
    }
  }

  const pairs: MatchOutput["pairs"] = [];
  const missed: number[] = [];
  const extra: number[] = [];
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    const op = bt[i]![j]!;
    if (op === 0) {
      pairs.push({
        e: expected[i - 1]!,
        d: detected[j - 1]!,
        ei: i - 1,
        di: j - 1,
      });
      i--;
      j--;
    } else if (op === 1) {
      missed.push(expected[i - 1]!);
      i--;
    } else {
      extra.push(detected[j - 1]!);
      j--;
    }
  }
  pairs.reverse();
  missed.reverse();
  extra.reverse();
  return { pairs, missed, extra };
}

/* ----------------------------------------------------------------- fitting */

interface Fit {
  a: number;
  b: number;
  fellBack: boolean;
}

/**
 * Bootstrap (a, b) before any matching exists.
 *
 * This has to be a search, not a fit. A fit needs pairs, and pairing needs a
 * rejection window, and a window can only be set once you know the tempo — a
 * player 8% fast over a 30s section is 2.4s out by the end, so an
 * identity-warp first pass simply fails to match the entire back half and
 * returns a ≈ 1.0. That is not a tuning problem; no window value fixes it,
 * because widening the window far enough to catch the tail also lets the
 * matcher pair words with their neighbours.
 *
 * So: grid-search tempo scale, and for each candidate let every (expected,
 * detected) pair vote for the offset it would imply. The winning cell is the
 * (a, b) under which the most onsets coincide — a Hough transform over the
 * two parameters we care about. Robust to misses, extras, and to the player
 * starting on the wrong word.
 */
function bootstrapFit(
  expected: number[],
  detected: number[],
  cfg: ScoringConfig,
): { a: number; b: number } {
  const { minTempoScale, maxTempoScale, tempoGridStep, maxOffset, offsetBinWidth } =
    cfg.fit;

  const nBins = Math.ceil((2 * maxOffset) / offsetBinWidth) + 1;
  // Votes are spread over neighbouring bins so a near-miss still counts; this
  // is a cheap stand-in for a kernel density estimate.
  const spread = Math.max(1, Math.round(cfg.match.fineWindow / 2 / offsetBinWidth));

  let best = { a: 1, b: 0, votes: -1 };
  const bins = new Float64Array(nBins);

  for (let a = minTempoScale; a <= maxTempoScale + 1e-9; a += tempoGridStep) {
    bins.fill(0);
    for (const e of expected) {
      const warped = a * e;
      for (const d of detected) {
        const diff = d - warped;
        if (diff < -maxOffset || diff > maxOffset) continue;
        const centre = Math.round((diff + maxOffset) / offsetBinWidth);
        const lo = Math.max(0, centre - spread);
        const hi = Math.min(nBins - 1, centre + spread);
        for (let k = lo; k <= hi; k++) {
          // Triangular weight: exact coincidences count more than near ones.
          bins[k]! += 1 - Math.abs(k - centre) / (spread + 1);
        }
      }
    }
    for (let k = 0; k < nBins; k++) {
      if (bins[k]! > best.votes) {
        best = { a, b: k * offsetBinWidth - maxOffset, votes: bins[k]! };
      }
    }
  }

  return { a: best.a, b: best.b };
}

/** Least squares d ≈ a*e + b, with guards against degenerate fits. */
function leastSquares(
  pairs: Array<{ e: number; d: number }>,
  cfg: ScoringConfig,
): Fit {
  const n = pairs.length;
  if (n === 0) return { a: 1, b: 0, fellBack: true };

  // Too few points to trust a slope: fit offset only.
  if (n < cfg.fit.minPairsForTempoFit) {
    const b = mean(pairs.map((p) => p.d - p.e));
    return { a: 1, b, fellBack: true };
  }

  const { a, b } = olsSlope(pairs);

  // A near-degenerate spread of expected onsets makes the slope meaningless
  // and lets clustered noise masquerade as consistency.
  const eSpread = Math.max(...pairs.map((p) => p.e)) - Math.min(...pairs.map((p) => p.e));
  if (!Number.isFinite(a) || eSpread < 1e-6) {
    return { a: 1, b: mean(pairs.map((p) => p.d - p.e)), fellBack: true };
  }

  if (a < cfg.fit.minTempoScale || a > cfg.fit.maxTempoScale) {
    // The player is outside any plausible tempo. Don't let the fit "rescue"
    // them by warping the reference to match — score against a = 1.
    return { a: 1, b: mean(pairs.map((p) => p.d - p.e)), fellBack: true };
  }

  if (cfg.fit.trimFraction > 0 && n >= 8) {
    const resid = pairs.map((p) => Math.abs(p.d - (a * p.e + b)));
    const keep = Math.max(4, Math.floor(n * (1 - cfg.fit.trimFraction)));
    const order = resid
      .map((r, idx) => [r, idx] as const)
      .sort((x, y) => x[0] - y[0])
      .slice(0, keep)
      .map(([, idx]) => idx);
    const trimmed = order.map((idx) => pairs[idx]!);
    const t = olsSlope(trimmed);
    if (
      Number.isFinite(t.a) &&
      t.a >= cfg.fit.minTempoScale &&
      t.a <= cfg.fit.maxTempoScale
    ) {
      return { a: t.a, b: t.b, fellBack: false };
    }
  }

  return { a, b, fellBack: false };
}

function olsSlope(pairs: Array<{ e: number; d: number }>): { a: number; b: number } {
  const n = pairs.length;
  const me = mean(pairs.map((p) => p.e));
  const md = mean(pairs.map((p) => p.d));
  let num = 0;
  let den = 0;
  for (const p of pairs) {
    num += (p.e - me) * (p.d - md);
    den += (p.e - me) ** 2;
  }
  if (den === 0) return { a: 1, b: md - me };
  const a = num / den;
  return { a, b: md - a * me };
  void n;
}

/* ------------------------------------------------------------------- score */

export interface ScoreInput {
  expected: number[];
  detected: number[];
  tier: Tier;
}

export function scoreAttempt(
  input: ScoreInput,
  cfg: ScoringConfig = DEFAULT_CONFIG,
): ScoreResult {
  const expected = [...input.expected].sort((x, y) => x - y);
  const detected = [...input.detected].sort((x, y) => x - y);
  const tierMultiplier = cfg.tierMultiplier[input.tier];

  if (expected.length === 0 || detected.length === 0) {
    return emptyResult(input.tier, tierMultiplier, {
      reason: "no-onsets",
      detail:
        detected.length === 0
          ? "No sung onsets were detected — check mic input and the VAD floor."
          : "Chart section contained no expected onsets.",
    });
  }

  // Bootstrap (a, b) by search, then refine by match -> least squares ->
  // rematch. Each rematch happens in the warped domain, where a
  // consistent-but-fast player looks tight and a drifting one does not.
  const seed = bootstrapFit(expected, detected, cfg);
  let fit: Fit = { a: seed.a, b: seed.b, fellBack: false };
  let match = matchMonotonic(
    expected,
    detected,
    cfg.match.coarseWindow,
    cfg,
    (e) => fit.a * e + fit.b,
  );

  for (let iter = 0; iter < cfg.match.iterations; iter++) {
    const refined = leastSquares(match.pairs, cfg);
    // Keep the bootstrap's tempo if least squares fell back — the search saw
    // all the data, whereas least squares only sees what already matched.
    fit = refined.fellBack ? { ...fit, b: refined.b } : refined;
    match = matchMonotonic(
      expected,
      detected,
      cfg.match.fineWindow,
      cfg,
      (e) => fit.a * e + fit.b,
    );
  }
  const finalFit = leastSquares(match.pairs, cfg);
  if (!finalFit.fellBack) fit = finalFit;
  else fit = { ...fit, b: finalFit.b };

  const coverage = match.pairs.length / expected.length;

  // Structural guard, and the main answer to the mumbling exploit: rhythmic
  // noise that doesn't track the actual word positions will fail to cover
  // enough expected onsets, and a tight residual over 30% of the section is
  // not an achievement. Scoring residuals only makes sense once we know the
  // player was actually singing the words.
  if (coverage < cfg.penalty.minCoverage) {
    const partial = emptyResult(input.tier, tierMultiplier, {
      reason: "coverage",
      detail: `Only ${(coverage * 100).toFixed(0)}% of expected onsets were matched (need ${(cfg.penalty.minCoverage * 100).toFixed(0)}%).`,
    });
    partial.coverage = coverage;
    partial.tempoScale = fit.a;
    partial.offset = fit.b;
    partial.summary.hitCount = match.pairs.length;
    partial.summary.missCount = match.missed.length;
    partial.summary.extraCount = match.extra.length;
    return partial;
  }

  // Per-onset points with the streak multiplier riding along.
  const pairs: MatchPair[] = [];
  let streak = 0;
  let bestStreak = 0;
  let peakMultiplier = 1;
  let earned = 0;
  let possible = 0;

  for (const p of match.pairs) {
    const residual = p.d - (fit.a * p.e + fit.b);
    const abs = Math.abs(residual);

    const base =
      abs >= cfg.residual.cutoff
        ? 0
        : Math.exp(-((abs / cfg.residual.sigma) ** 2));

    if (abs <= cfg.streak.threshold) {
      streak++;
      bestStreak = Math.max(bestStreak, streak);
    } else {
      streak = 0;
    }

    const mult =
      streak >= cfg.streak.minLength
        ? Math.min(
            cfg.streak.max,
            1 + (streak - cfg.streak.minLength + 1) * cfg.streak.growth,
          )
        : 1;
    peakMultiplier = Math.max(peakMultiplier, mult);

    const points = base * mult;
    earned += points;
    // Denominator uses mult too, so a perfect run scores 100 rather than
    // 100 * peakMultiplier — the streak redistributes weight toward held
    // clocks, it doesn't inflate the ceiling.
    possible += mult;

    pairs.push({
      expectedIndex: p.ei,
      detectedIndex: p.di,
      expected: p.e,
      detected: p.d,
      residual,
      rawDeviation: p.d - p.e,
      points,
      streakAtThisPoint: streak,
      multiplierApplied: mult,
    });
  }

  // Timing quality over the onsets they actually sang, 0..1.
  const timingQuality = possible > 0 ? clamp01(earned / possible) : 0;

  // Penalties are multiplicative factors on timing quality, not extra terms in
  // the denominator. Folding them into `possible` lets a long streak inflate
  // the denominator and dilute the penalty — measured: 34 spurious onsets cost
  // 12 points that way, 21 points this way.

  // Misses are charged per word: skipping a word is a discrete failure, and
  // each one is equally a failure.
  const missFraction =
    (match.missed.length * cfg.penalty.perMiss) / Math.max(1, expected.length);
  const missFactor = clamp01(1 - missFraction);

  // Extras are charged on precision. See the config comment: a linear
  // per-extra cost lets a fast mumbler buy coverage faster than it costs them,
  // and pricing it high enough to stop that punishes real singers for
  // breathing. Precision scales with how much they sprayed relative to how
  // much they landed, which is the distinction that actually matters.
  const graceCount = Math.floor(expected.length * cfg.penalty.extraGracePct);
  const chargeableExtras = Math.max(0, match.extra.length - graceCount);
  const precision =
    pairs.length > 0
      ? pairs.length / (pairs.length + chargeableExtras)
      : 0;
  const precisionFactor = Math.pow(precision, cfg.penalty.precisionWeight);

  const baseScore = timingQuality * missFactor * precisionFactor * 100;

  const residuals = pairs.map((p) => p.residual);
  const score = baseScore * tierMultiplier;

  return {
    baseScore,
    score,
    tier: input.tier,
    tierMultiplier,
    tempoScale: fit.a,
    offset: fit.b,
    tempoFitFellBack: fit.fellBack,
    pairs,
    missedExpected: match.missed,
    extraDetected: match.extra,
    coverage,
    residualRms: rms(residuals),
    residualMedianAbs: median(residuals.map(Math.abs)),
    bestStreak,
    peakMultiplier,
    rejected: null,
    summary: {
      rushedPct: (fit.a - 1) * 100,
      earlyMs: fit.b * 1000,
      hitCount: pairs.length,
      missCount: match.missed.length,
      extraCount: match.extra.length,
    },
  };
}

/* ------------------------------------------------------------------- utils */

function emptyResult(
  tier: Tier,
  tierMultiplier: number,
  rejected: ScoreResult["rejected"],
): ScoreResult {
  return {
    baseScore: 0,
    score: 0,
    tier,
    tierMultiplier,
    tempoScale: 1,
    offset: 0,
    tempoFitFellBack: true,
    pairs: [],
    missedExpected: [],
    extraDetected: [],
    coverage: 0,
    residualRms: 0,
    residualMedianAbs: 0,
    bestStreak: 0,
    peakMultiplier: 1,
    rejected,
    summary: {
      rushedPct: 0,
      earlyMs: 0,
      hitCount: 0,
      missCount: 0,
      extraCount: 0,
    },
  };
}

function mean(xs: number[]): number {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
}

function rms(xs: number[]): number {
  return xs.length ? Math.sqrt(mean(xs.map((x) => x * x))) : 0;
}

function median(xs: number[]): number {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}
